import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type RootState from 'app-components/utils/redux-store/rootState';

import { compareReportConfigs } from '../defaultReportConfigurations';
import { getResourcesById as getDefaultsById } from '../defaults/reportConfigurationDefaultsSelectors';
import { hasSavedReportParent } from '../reportConfigurationsConstants';
import { getReportConfigById } from '../reportConfigurationsSelectors';
import type { ReportConfiguration } from '../reportConfigurationsTypes';

export type UseIsConfigModifiedResult = {
  isModified: boolean;
  parentConfig: ReportConfiguration | undefined;
  isSavedConfig: boolean;
};

/**
 * Hook to detect whether a report configuration has been modified from its parent.
 *
 * Works for OPEN and TEMPORARY configs with any parentPersistentType (PREDEFINED, SAVED, CUSTOM).
 * `isSavedConfig` is true only when the parent is SAVED or CUSTOM.
 */
export const useIsConfigModified = (reportConfigId: string): UseIsConfigModifiedResult => {
  const reportConfig = useSelector((state: RootState) => getReportConfigById(state, { id: reportConfigId }));

  const parentConfigId = reportConfig?.parentConfigId;
  const isOpenWithParent =
    (reportConfig?.persistentType === 'OPEN' || reportConfig?.persistentType === 'TEMPORARY') && !!parentConfigId;
  const isSavedConfig = isOpenWithParent && hasSavedReportParent(reportConfig);

  const parentConfig = useSelector((state: RootState) => {
    if (!isOpenWithParent || !parentConfigId) return undefined;

    // Try main configs store first
    const fromConfigs = getReportConfigById(state, { id: parentConfigId });
    if (fromConfigs) return fromConfigs as ReportConfiguration;

    // Fall back to defaults store for PREDEFINED parents
    const defaultsById = getDefaultsById(state);
    return defaultsById?.get(parentConfigId) as ReportConfiguration | undefined;
  });

  const isModified = useMemo(() => {
    if (!isOpenWithParent || !reportConfig || !parentConfig) return false;

    // Exclude 'name' (OPEN config may have different name from parent).
    // For TRANSACTION reports, also exclude 'timestep' (matching mobile logic).
    const excludeFields = reportConfig.reportType === 'TRANSACTION' ? ['name', 'timestep'] : ['name'];

    return !compareReportConfigs(reportConfig, parentConfig, excludeFields);
  }, [isOpenWithParent, reportConfig, parentConfig]);

  return { isModified, parentConfig, isSavedConfig };
};
